'use client';

import type { Member } from '@slims/shared/types';
import { Button, Stack } from '@slims/ui/atoms';
import {
	ConfirmationDialog,
	MemberInviteButton,
	StatusBlock,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@slims/ui/molecules';
import type { ConsumptionQ } from '@slims/ui/organisms';
import { PageContainer, PageHeader } from '@slims/ui/templates';
import { Save, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { GatewayCredentialDialog } from '@/components/members/gateway-credential-dialog';
import { AccumuloTab } from '@/components/members/tabs/accumulo-tab';
import { AnagraficaTab } from '@/components/members/tabs/anagrafica-tab';
import { ConnessioneConsumoTab } from '@/components/members/tabs/connessione-consumo-tab';
import { HealthcheckTab } from '@/components/members/tabs/healthcheck-tab';
import { ProduzioneTab } from '@/components/members/tabs/produzione-tab';
import { UtenteTab } from '@/components/members/tabs/utente-tab';
import { useMembers } from '@/hooks/use-members';
import { useAdminScopeStore } from '@/stores/admin-scope-store';
import { type Meter, useMeters } from '@/hooks/use-meters';
import { useBlocker, useParams, useRouter } from '@/lib/next/navigation';
import { api, apiFetch } from '@/lib/utils/api';

// Member fields persisted as plain strings; these are coerced to the right shape.
const BOOL_KEYS = new Set(['hasStorage']);
// Timestamp columns: an empty string is invalid and must be sent as null.
// Bound to an <input type="date">, which only accepts YYYY-MM-DD: the full ISO
// value from the API is truncated on load.
const DATE_KEYS = new Set(['dataIscrizione', 'dataRecesso']);

type Form = Record<string, string | boolean>;

// Solo i dati che servono all'energia: anagrafica fiscale, documenti, IBAN,
// contatti, sede e stato di adesione sono stati tolti con la parte burocratica.
// L'uscita dalla CER è una data (`dataRecesso`): lo stato lo deriva l'API.
const MEMBER_KEYS = ['nome', 'tipo', 'ruolo', 'email', 'hasStorage', 'dataIscrizione', 'dataRecesso'];

/**
 * Full-page tabbed Member Details (Dettagli Socio). Mirrors the CER/Config
 * Dettagli pages but groups member, metering and telemetry data into tabs.
 */
export default function MemberDetailsPage() {
	const { id } = useParams<{ id: string }>();
	const router = useRouter();
	const [form, setForm] = useState<Form>({});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [gatewayMeter, setGatewayMeter] = useState<Meter | null>(null);
	const [consProfile, setConsProfile] = useState('');
	const [consData, setConsData] = useState<Partial<ConsumptionQ> | null>(null);
	// Ogni tab registra qui il proprio salvataggio (misuratore, e per Consumo
	// anche il questionario): "Salva" in alto li richiama tutti prima di
	// salvare l'anagrafica, cosicché un solo click salvi davvero tutto ciò che
	// è stato compilato nei vari tab, non solo l'anagrafica.
	const m1SaveRef = useRef<(() => Promise<boolean>) | null>(null);
	const m2SaveRef = useRef<(() => Promise<boolean>) | null>(null);
	const m3SaveRef = useRef<(() => Promise<boolean>) | null>(null);
	// Modifiche non salvate (R-07, INB-393): uscire dalla scheda le scartava in
	// silenzio. Ogni scheda di misuratore dice se la sua bozza è aperta;
	// l'anagrafica si confronta con quella caricata. Sono ref perché li legge il
	// blocco della navigazione al momento dell'uscita, non il render.
	const meterDirtyRef = useRef({ m1: false, m2: false, m3: false });
	const savedFormRef = useRef<Form>({});
	const formRef = useRef<Form>({});
	// Dopo un salvataggio riuscito si torna all'elenco senza chiedere nulla.
	const leavingRef = useRef(false);
	const proceedingRef = useRef(false);
	// Il socio così come l'API lo restituisce. Il form lo appiattisce in stringhe
	// per gli input; la tab Utente ha bisogno della riga vera — `userId` e `email`
	// decidono cosa mostrare — quindi la si tiene accanto invece di ricostruirla.
	const [member, setMember] = useState<Member | null>(null);

	// Nessun `addMeter`: i punti di misura nascono col socio, dal suo ruolo.
	const { meters, updateMeter, refetch } = useMeters(id);
	const adminConfigId = useAdminScopeStore((s) => s.configId);
	const { inviteMember, associateUser } = useMembers(adminConfigId);

	async function sendInviteEmail(args: { memberId?: string; link: string | null; memberName?: string }) {
		const res = await apiFetch(`/api/members/invite/email?orgId=${adminConfigId}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(args),
		});
		const json = await res.json();
		return { status: res.status, ok: res.ok, ...json };
	}

	const v = (k: string) => String(form[k] ?? '');
	const b = (k: string) => Boolean(form[k]);
	const set = (k: string, val: string | boolean) => setForm((f) => ({ ...f, [k]: val }));

	formRef.current = form;
	const hasUnsavedChanges = () => {
		if (leavingRef.current) return false;
		const { m1, m2, m3 } = meterDirtyRef.current;
		if (m1 || m2 || m3) return true;
		const saved = savedFormRef.current;
		return Object.entries(formRef.current).some(([k, val]) => (saved[k] ?? '') !== val);
	};
	// Menu, «Annulla», tasto indietro: dentro l'app la navigazione si ferma e si
	// chiede conferma. Cambiare scheda non è un'uscita.
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			currentLocation.pathname !== nextLocation.pathname && hasUnsavedChanges(),
	);
	// Chiudere o ricaricare la scheda del browser: lì c'è solo l'avviso del browser.
	useEffect(() => {
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!hasUnsavedChanges()) return;
			event.preventDefault();
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, []);
	const markMeterDirty = (key: 'm1' | 'm2' | 'm3') => (dirty: boolean) => {
		meterDirtyRef.current = { ...meterDirtyRef.current, [key]: dirty };
	};

	const loadMember = useCallback(async () => {
		if (!id) return;
		try {
			const d = await api.get<Record<string, unknown>>(
				`/api/members/${id}?orgId=${adminConfigId}`,
			);
			const next: Form = {};
			for (const k of MEMBER_KEYS) {
				if (BOOL_KEYS.has(k)) {
					next[k] = Boolean(d[k]);
				} else if (d[k] == null) {
					next[k] = '';
				} else {
					const raw = String(d[k]);
					next[k] = DATE_KEYS.has(k) ? raw.slice(0, 10) : raw;
				}
			}
			setForm(next);
			savedFormRef.current = next;
			setMember(d as unknown as Member);
			setConsProfile(d.consumptionProfile == null ? '' : String(d.consumptionProfile));
			setConsData((d.consumptionProfileData as Partial<ConsumptionQ> | null) ?? null);
		} finally {
			setLoading(false);
		}
	}, [id, adminConfigId]);

	useEffect(() => {
		void loadMember().catch(() => setLoading(false));
	}, [loadMember]);

	// Classify meters by kind, falling back to tipo for legacy rows without kind.
	// When a member has duplicate meters of the same kind (e.g. a double-submit
	// left an empty stub alongside the real one), prefer the one that carries a
	// POD codice so the configured meter — not the blank dup — drives the tab.
	const pick = useMemo(
		() => (match: (m: Meter) => boolean) => {
			const matches = meters.filter(match);
			return matches.find((m) => m.codice?.trim()) ?? matches[0];
		},
		[meters],
	);
	const m1 = useMemo(
		() => pick((m) => m.kind === 'M1' || (!m.kind && m.tipo === 'withdrawal')),
		[pick],
	);
	const m2 = useMemo(
		() => pick((m) => m.kind === 'M2' || (!m.kind && m.tipo === 'injection')),
		[pick],
	);
	const m3 = useMemo(() => pick((m) => m.kind === 'M3' || m.tipo === 'storage'), [pick]);

	// I punti di misura li decide il ruolo, quindi le schede seguono il ruolo: un
	// produttore puro non ha un prelievo da mostrare, e una scheda che esiste solo
	// per dire «non per questo socio» è rumore.
	const ruolo = v('ruolo');
	const hasStorage = b('hasStorage');
	const showConsumo = ruolo === 'consumer' || ruolo === 'prosumer';
	const showProduzione = ruolo === 'producer' || ruolo === 'prosumer';
	// L'accumulo sta accanto a un impianto: un consumatore puro non ne ha.
	const showAccumulo = hasStorage && ruolo !== 'consumer';

	// Cambiando il ruolo dal menu, o la spunta «Sistema di accumulo», il punto di
	// misura che implicano viene creato **subito** lato server (PATCH di `ruolo`
	// e `hasStorage`, che l'API non rifiuta perché aggiunge e non toglie), così
	// la scheda M1/M2/M3 è compilabile senza salvare, uscire e rientrare — prima
	// valeva solo per il ruolo, e la spunta apriva un tab con solo un messaggio
	// vuoto finché non si passava dal "Salva" in alto. Un `Set` sulla coppia
	// ruolo+accumulo evita di ripetere la chiamata quando i misuratori tornano
	// dal refetch.
	const roleSyncedRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (loading || !id || !adminConfigId) return;
		if (!ruolo) return;
		const syncKey = `${ruolo}:${hasStorage}`;
		if (roleSyncedRef.current.has(syncKey)) return;
		const needsM1 = ruolo === 'consumer' || ruolo === 'prosumer';
		const needsM2 = ruolo === 'producer' || ruolo === 'prosumer';
		const needsM3 = hasStorage;
		const haveM1 = meters.some((m) => m.kind === 'M1' || (!m.kind && m.tipo === 'withdrawal'));
		const haveM2 = meters.some((m) => m.kind === 'M2' || (!m.kind && m.tipo === 'injection'));
		const haveM3 = meters.some((m) => m.kind === 'M3' || m.tipo === 'storage');
		if (!((needsM1 && !haveM1) || (needsM2 && !haveM2) || (needsM3 && !haveM3))) return;

		roleSyncedRef.current.add(syncKey);
		void (async () => {
			try {
				const res = await apiFetch(`/api/members/${id}?orgId=${adminConfigId}`, {
					method: 'PATCH',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ ruolo, hasStorage }),
				});
				if (res.ok) {
					// Ruolo e accumulo sono già scritti: non sono più modifiche in sospeso.
					savedFormRef.current = { ...savedFormRef.current, ruolo, hasStorage };
					await refetch();
				}
				// Fallito: consenti che il salvataggio esplicito riprovi il sync.
				else roleSyncedRef.current.delete(syncKey);
			} catch {
				roleSyncedRef.current.delete(syncKey);
			}
		})();
	}, [loading, id, adminConfigId, ruolo, hasStorage, meters, refetch]);

	async function handleSave() {
		if (!id) return;
		setSaving(true);
		// Prima i misuratori (e per Consumo, il questionario): ognuno mostra il
		// proprio errore se fallisce (INB-142), quindi qui basta fermarsi — salvare
		// comunque l'anagrafica e navigare via lascerebbe quella bozza indietro.
		for (const ref of [m1SaveRef, m2SaveRef, m3SaveRef]) {
			if (ref.current && !(await ref.current())) {
				setSaving(false);
				return;
			}
		}
		const body: Record<string, unknown> = {};
		for (const [k, val] of Object.entries(form)) {
			// Timestamp columns reject '' with "invalid input syntax for type
			// timestamp" → send null. Other empty strings are left as-is.
			body[k] = BOOL_KEYS.has(k) ? Boolean(val) : DATE_KEYS.has(k) && val === '' ? null : val;
		}
		try {
			const res = await apiFetch(`/api/members/${id}?orgId=${adminConfigId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			if (res.ok) {
				// Cambiare ruolo può portarsi via i punti di misura che il nuovo ruolo
				// non prevede: sono spariti per conseguenza, non per richiesta, quindi
				// il toast lo dice invece di lasciarlo scoprire dalle schede.
				const saved = (await res.json().catch(() => null)) as {
					metersRemoved?: string[];
				} | null;
				const rimossi = saved?.metersRemoved ?? [];
				toast.success(
					rimossi.length > 0
						? `Dettagli socio salvati · rimosso ${rimossi.join(', ')}`
						: 'Dettagli socio salvati',
				);
				// Salvato → torna all'elenco soci (non si resta sul dettaglio).
				leavingRef.current = true;
				router.push('/members');
				return;
			} else {
				// L'API rifiuta un'email non valida e i cambi di ruolo che lascerebbero
				// orfani dati reali: mostrarne il motivo invece di un errore generico.
				const err = (await res.json().catch(() => null)) as { error?: string } | null;
				toast.error(err?.error ?? 'Errore nel salvataggio');
			}
		} catch {
			toast.error('Errore di rete');
		} finally {
			setSaving(false);
		}
	}

	if (loading) {
		return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	}

	return (
		<PageContainer width="md">
			<Stack gap="xl">
			<PageHeader
				stackOnMobile
				size="medium"
				title={v('nome') || 'Socio'}
				actions={
					<>
						{id && (
							<MemberInviteButton
								memberId={id}
								memberName={v('nome')}
								email={v('email') || null}
								onInvite={inviteMember}
								onSendInviteEmail={sendInviteEmail}
								invitedAt={member?.invitedAt ?? null}
							/>
						)}
						<Button
							type="button"
							variant="outline"
							onClick={() => router.push('/members')}
							disabled={saving}
						>
							<X />
							Annulla
						</Button>
						<Button type="button" variant="accent" onClick={handleSave} disabled={saving}>
							<Save />
							{saving ? 'Salvataggio...' : 'Salva'}
						</Button>
					</>
				}
			/>

			{/* `forceMount` sulle schede dei punti di misura: tengono un buffer di
			    modifica locale (`useMeterDraft`, editor del profilo consumi) che
			    Radix distruggerebbe smontando la scheda al cambio tab — un campo
			    compilato e non ancora salvato andava perso passando a un'altra
			    scheda. Le altre schede leggono/scrivono lo stato del form di
			    pagina, quindi non serve. */}
			<Tabs defaultValue="anagrafica">
				<TabsList wrap>
					<TabsTrigger value="anagrafica">Anagrafica</TabsTrigger>
					{showConsumo && <TabsTrigger value="consumo">Consumo</TabsTrigger>}
					{showProduzione && <TabsTrigger value="produzione">Produzione</TabsTrigger>}
					{showAccumulo && <TabsTrigger value="accumulo">Accumulo</TabsTrigger>}
					{/* Subito dopo le schede dei punti di misura: è la verifica di ciò che
					    quelle schede configurano, e prima delle voci amministrative. */}
					<TabsTrigger value="healthcheck">Healthcheck</TabsTrigger>
					<TabsTrigger value="utente">Utente</TabsTrigger>
				</TabsList>

				<TabsContent value="anagrafica">
					<AnagraficaTab v={v} set={set} b={b} />
				</TabsContent>

				{showConsumo && (
					<TabsContent value="consumo" forceMount>
						<ConnessioneConsumoTab
							meter={m1}
							onUpdate={updateMeter}
							onOpenGateway={setGatewayMeter}
							ruolo={ruolo}
							tipo={v('tipo')}
							hasStorage={b('hasStorage')}
							memberId={id ?? ''}
							consumptionProfile={consProfile}
							consumptionData={consData}
							dispositivi={member?.dispositivi}
							onRefetchMeters={refetch}
							onRegisterSave={(save) => {
								m1SaveRef.current = save;
							}}
							onDirtyChange={markMeterDirty('m1')}
						/>
					</TabsContent>
				)}

				{showProduzione && (
					<TabsContent value="produzione" forceMount>
						<ProduzioneTab
							meter={m2}
							onUpdate={updateMeter}
							onOpenGateway={setGatewayMeter}
							ruolo={ruolo}
							tipo={v('tipo')}
							hasStorage={b('hasStorage')}
							onRegisterSave={(save) => {
								m2SaveRef.current = save;
							}}
							onDirtyChange={markMeterDirty('m2')}
						/>
					</TabsContent>
				)}

				{showAccumulo && (
					<TabsContent value="accumulo" forceMount>
						<AccumuloTab
							meter={m3}
							onUpdate={updateMeter}
							onRegisterSave={(save) => {
								m3SaveRef.current = save;
							}}
							onDirtyChange={markMeterDirty('m3')}
						/>
					</TabsContent>
				)}

				<TabsContent value="healthcheck">{id && <HealthcheckTab memberId={id} />}</TabsContent>

				<TabsContent value="utente">
					<UtenteTab member={member} onAssociateUser={associateUser} onLinked={loadMember} />
				</TabsContent>
			</Tabs>

			<ConfirmationDialog
				open={blocker.state === 'blocked'}
				onOpenChange={(open) => {
					// Il dialog si chiude anche dopo «Esci»: lì la navigazione è già
					// ripartita e `reset` la annullerebbe.
					if (!open && !proceedingRef.current && blocker.state === 'blocked') blocker.reset();
				}}
				title="Uscire senza salvare?"
				description="Hai modifiche a questo socio non ancora salvate. Uscendo andranno perse: per tenerle, resta e premi «Salva»."
				confirmText="Esci senza salvare"
				cancelText="Resta"
				onConfirm={() => {
					if (blocker.state !== 'blocked') return;
					proceedingRef.current = true;
					blocker.proceed();
				}}
				variant="destructive"
			/>

			{gatewayMeter && (
				<GatewayCredentialDialog
					meterId={gatewayMeter.id}
					meterCodice={gatewayMeter.codice || gatewayMeter.kind || 'Misuratore'}
					open={!!gatewayMeter}
					onClose={() => setGatewayMeter(null)}
					onRegistered={refetch}
				/>
			)}
			</Stack>
		</PageContainer>
	);
}
